import { ICoreContext } from '@msdyn365-commerce/core';
import { IRequestContext } from '@msdyn365-commerce/core-internal';

import { clearCookie } from '../../functions/cookie';
import { UserCentricsService } from '../../../integrations/userCentrics/UserCentricsService';
import { CookieNames } from '../cookie-service/cookie-service.const';
import { CookieService } from '../cookie-service/cookie-service';
import {
    GENERIC_FEMALE_PATHS,
    GENERIC_MALE_PATHS,
    MIN_GENDERS_FOR_ENABLING_GENDER_FEATURES,
    SEARCH_GENDERS
} from './gender-cookie-service.const';
import { Gender, IGenderPathMatch, ILocaleGenderUrls } from './gender-cookie-service.types';
import { isInHomePage } from '../../functions/msdyn-helpers';
import { areUrlsEqual } from '../../functions/url-helpers';
import { parseFilterOptions } from '../../../utils/productListFilters';
import { GENDER_COOKIE_CHANGED_EVENT } from '../../const/events';

const trimSlashes = (path: string): string => path.trim().replace(/^\/+|\/+$/g, '');

const normalizeGenderPath = (path: string): string => trimSlashes(path).toLowerCase();

export const getGenderUrlsForLocale = (
    localeGenderUrls: ILocaleGenderUrls[] | undefined,
    locale: string | undefined
): ILocaleGenderUrls | undefined => localeGenderUrls?.find(genderUrls => genderUrls.locale === locale);

export const getAvailableGendersForLocale = (localeGenderUrls: ILocaleGenderUrls[] | undefined, locale: string | undefined): Gender[] => {
    const genderUrls = getGenderUrlsForLocale(localeGenderUrls, locale);
    return genderUrls ? SEARCH_GENDERS.filter(gender => !!genderUrls[gender]?.trim()) : [];
};

export const areGenderFeaturesEnabled = (request: IRequestContext): boolean => {
    const gendersAvailableForLocale = getAvailableGendersForLocale(request.app.config.localeGenderUrls, request.locale);
    return gendersAvailableForLocale.length >= MIN_GENDERS_FOR_ENABLING_GENDER_FEATURES;
};

/**
 * Detects a gender context from a pathname by comparing complete, normalized path segments
 * against the configured locale paths plus the generic aliases (female: 'ladies'; male: 'men', 'mens').
 * Substring matches are intentionally not considered.
 */
export const detectGenderFromPath = (pathname: string, genderUrls: ILocaleGenderUrls): IGenderPathMatch | undefined => {
    const segments = pathname
        .split('/')
        .map(normalizeGenderPath)
        .filter(segment => segment !== '');

    const femalePaths = [genderUrls.female, ...GENERIC_FEMALE_PATHS].map(normalizeGenderPath);
    if (segments.some(segment => femalePaths.includes(segment))) {
        return { gender: 'female', genderPath: genderUrls.female };
    }

    const malePaths = [genderUrls.male, ...GENERIC_MALE_PATHS].map(normalizeGenderPath);
    if (segments.some(segment => malePaths.includes(segment))) {
        return { gender: 'male', genderPath: genderUrls.male };
    }

    return undefined;
};

/**
 * Joins the site base URL and a gender path without duplicate slashes.
 */
export const buildGenderUrl = (baseUrl: string, genderPath: string): string => {
    const base = baseUrl.trim().replace(/\/+$/, '');
    return `${base}/${trimSlashes(genderPath)}`;
};

export const getHomepageGender = (context: ICoreContext): Gender | undefined => {
    if (!isInHomePage(context)) {
        return;
    }

    const { request } = context;
    const genderUrls = getGenderUrlsForLocale(request.app.config.localeGenderUrls, request.locale);
    const { pathname } = request.url.requestUrl;
    if (!genderUrls || areUrlsEqual(request.app.routes.home.destinationUrl, pathname)) {
        return;
    }

    const match = detectGenderFromPath(pathname, genderUrls);
    return match?.genderPath ? match.gender : undefined;
};

export const getPageGenderFromUrl = (request: IRequestContext): Gender | undefined => {
    const queryGender = parseFilterOptions(key => request.query?.[key])?.gender;
    if (queryGender) {
        return queryGender;
    }

    const genderUrls = getGenderUrlsForLocale(request.app.config.localeGenderUrls, request.locale);
    const match = genderUrls ? detectGenderFromPath(request.url.requestUrl.pathname, genderUrls) : undefined;
    return match?.genderPath ? match.gender : undefined;
};

export const getGenderCookie = (request: IRequestContext): Gender | undefined => {
    const cookieService = new CookieService(request);
    const value = cookieService.getRequestCookie(CookieNames.Gender);
    return value === 'male' || value === 'female' ? value : undefined;
};

// Legacy cookie was written to sitePath instead of root url, which doesn't mesh well with MS's cookie methods
// TODO: Remove once the old cookies have expired for existing users: one month after this is deployed.
export const clearLegacyGenderCookie = (request: IRequestContext): void => {
    if (typeof window === 'undefined') {
        return;
    }
    clearCookie(CookieNames.Gender, request.sitePath);
};

export const syncGenderCookie = async (context: ICoreContext, gender: Gender | undefined): Promise<Gender | undefined> => {
    if (typeof window === 'undefined') {
        return getGenderCookie(context.request);
    }

    clearLegacyGenderCookie(context.request);

    const cookieService = new CookieService(context.request);
    const functionalConsent = await UserCentricsService.hasFunctionalConsent();

    const isConsentUnknown = functionalConsent === undefined;
    if (isConsentUnknown) {
        return getGenderCookie(context.request);
    }

    if (!functionalConsent) {
        cookieService.removeRequestCookie(CookieNames.Gender);
        window.dispatchEvent(new Event(GENDER_COOKIE_CHANGED_EVENT));
        return undefined;
    }

    if (!gender) {
        return getGenderCookie(context.request);
    }

    const shouldUpdateGenderCookie = getGenderCookie(context.request) !== gender;
    if (shouldUpdateGenderCookie) {
        await cookieService.setRequestCookie(CookieNames.Gender, gender);
        window.dispatchEvent(new Event(GENDER_COOKIE_CHANGED_EVENT));
    }

    return gender;
};
